import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { DateUtils } from 'react-day-picker'
import i18n, { t } from '@sportninja/common/i18n'

import { toISOString } from '../../utils/utils'
import Picker from '../Picker'
import YearMonthPicker from './YearMonthPicker'
import TimePicker from './TimePicker'
import { StyledDayPicker, PickerContents } from './css'
import translations from './translations.json'
import Icon from '../Icon'

dayjs.extend(utc)

const isUndefined = (o) => typeof o === 'undefined'
export const datePickerRequired = (values) => {
  const s = isUndefined(values.starts_at),
    e = isUndefined(values.ends_at)
  let message = false

  if (s && e) message = t('errors:startAndEndDate')
  else if (!s && e) message = t('errors:endDate')

  return message && { starts_at: message }
}

const isSelectingFirstDay = (from, to, day) => {
  const isBeforeFirstDay = from && DateUtils.isDayBefore(day, from)
  const isRangeSelected = from && to
  return !from || isBeforeFirstDay || isRangeSelected
}

const DatePicker = ({
  changeOnReset = false,
  datePlaceholder,
  defaultValue,
  defaultValues = {},
  disabled,
  errors = {},
  hasError,
  hideClear = false,
  hideTime = false,
  displayMonth,
  name,
  noFlex,
  onChange,
  label,
  required,
  useRange = false,
  useISOString = true,
  overrideStyles = {},
}) => {
  const today = new Date()
  const initialMonth =
    displayMonth instanceof Date
      ? displayMonth
      : new Date(today.getFullYear(), today.getMonth())

  const pickerRef = useRef(null)

  // Probably an elegant way to fix this - but we don't want to display the current
  // date/time if there's no default value - so we pass in undefined.
  const [day, setDay] = useState(
    defaultValue ? dayjs(defaultValue).toDate() : undefined
  )
  const [month, setMonth] = useState(initialMonth)

  const a =
    defaultValues.starts_at &&
    defaultValues.starts_at.value &&
    dayjs(defaultValues.starts_at.value).utc()
  const b =
    defaultValues.ends_at &&
    defaultValues.ends_at.value &&
    dayjs(defaultValues.ends_at.value).utc()

  // The DayPicker component wants native Javascript date objects - but we also
  // want to avoid using a local date. So, explicitly pass the UTC year, month,
  // and date to JS.
  const startsAtDefaultValue = a
    ? new Date(a.year(), a.month(), a.date())
    : undefined
  const endsAtDefaultValue = b
    ? new Date(b.year(), b.month(), b.date())
    : undefined

  const [from, setFrom] = useState(startsAtDefaultValue)
  const [to, setTo] = useState(endsAtDefaultValue)
  const [enteredTo, setEnteredTo] = useState(endsAtDefaultValue)

  const [time, setTime] = useState([
    day instanceof Date ? day.getHours() : today.getHours(),
    day instanceof Date ? day.getMinutes() : today.getMinutes(),
  ])

  useEffect(() => {
    if (typeof defaultValue === 'string') {
      const defaultDate = dayjs(defaultValue).toDate()
      setDay(defaultDate)
      setMonth(new Date(defaultDate.getFullYear(), defaultDate.getMonth()))
    }
  }, [defaultValue])

  useEffect(() => {
    if (Object.keys(defaultValues).length > 0 || useRange) {
      const a =
        defaultValues.starts_at &&
        defaultValues.starts_at.value &&
        dayjs(defaultValues.starts_at.value).utc()
      const b =
        defaultValues.ends_at &&
        defaultValues.ends_at.value &&
        dayjs(defaultValues.ends_at.value).utc()
      const startsAtDefaultValue = a
        ? new Date(a.year(), a.month(), a.date())
        : undefined
      const endsAtDefaultValue = b
        ? new Date(b.year(), b.month(), b.date())
        : undefined

      setFrom(startsAtDefaultValue)
      setTo(endsAtDefaultValue)
      setEnteredTo(endsAtDefaultValue)
      setDay(startsAtDefaultValue)
      if (startsAtDefaultValue instanceof Date)
        setMonth(
          new Date(
            startsAtDefaultValue.getFullYear(),
            startsAtDefaultValue.getMonth()
          )
        )
      else {
        setMonth(displayMonth)
      }
    }
  }, [defaultValues, useRange])

  const resetDates = (e) => {
    if (e) {
      e.preventDefault()
      e.stopPropagation()
      e.nativeEvent.stopImmediatePropagation()
    }

    if (useRange) {
      setFrom(null)
      setTo(null)
      setEnteredTo(null)
      onChange({ target: { name: 'starts_at', value: undefined } })
      onChange({ target: { name: 'ends_at', value: undefined } })
    } else {
      setDay(undefined)
      if (changeOnReset) onChange({ target: { name, value: undefined } })
    }
  }

  const handleChange = (day) => {
    if (useRange) {
      if (isSelectingFirstDay(from, to, day) || (from && to)) {
        setFrom(day)
        setTo(null)
        setEnteredTo(null)
        onChange({
          target: {
            name: 'starts_at',
            value: day ? toISOString(dayjs(day)) : undefined,
          },
        })
        onChange({ target: { name: 'ends_at', value: undefined } })
        setDay(day)
        setMonth(new Date(day.getFullYear(), day.getMonth()))
      } else {
        setTo(day)
        setEnteredTo(day)
        onChange({
          target: {
            name: 'ends_at',
            value: day ? toISOString(dayjs(day)) : undefined,
          },
        })
      }
    } else {
      const newDay = new Date(day)
      newDay.setHours(time[0])
      newDay.setMinutes(time[1])

      setDay(newDay)
      onChange({
        target: {
          name,
          value: newDay
            ? useISOString
              ? toISOString(dayjs(newDay))
              : dayjs(newDay)
            : undefined,
        },
      })
    }
  }

  const handleTimeChange = (hour, minute) => {
    setTime([hour, minute])
    const newDay = day ? new Date(day) : new Date()

    newDay.setHours(hour)
    newDay.setMinutes(minute)

    setDay(newDay)
    onChange({ target: { name, value: toISOString(dayjs(newDay)) } })
  }

  let additionalProps = {}
  if (useRange) {
    const modifiers = { start: from, end: enteredTo }
    // const disabledDays = { before: from }
    const selectedDays = [from, { from, to: enteredTo }]

    additionalProps = {
      selectedDays: selectedDays,
      // disabledDays: disabledDays,
      modifiers: modifiers,
    }
  }

  const sharedInputProps = {
    disabled,
    hasError: hasError || !!errors.starts_at,
    noFlex,
    required,
  }

  let localeProps = {}
  if (
    i18n.locale !== 'en' &&
    Object.prototype.hasOwnProperty.call(translations, i18n.locale)
  ) {
    localeProps = translations[i18n.locale]
  }

  return (
    <Picker
      disabled={disabled}
      hideClear={hideClear}
      label={label}
      onReset={resetDates}
      placeholder={datePlaceholder}
      value={
        useRange
          ? from &&
            `${from.toLocaleDateString()} - ${
              to ? to.toLocaleDateString() : ''
            }`
          : day &&
            dayjs(day).format(`MMM D, YYYY${!hideTime ? ' - h:mm a' : ''}`)
      }
      icon={
        <Icon
          faType='far'
          name='calendar'
          className='date-picker-icon'
          color='white'
        />
      }
      {...sharedInputProps}
    >
      <PickerContents ref={pickerRef} className='date-picker-contents' style={overrideStyles}>
        <StyledDayPicker
          {...localeProps}
          locale={i18n.locale}
          className={useRange ? 'range' : undefined}
          // enableOutsideDaysClick
          // showOutsideDays
          selectedDays={day}
          // This is really a Date object representing the month of a given year
          month={month}
          onDayClick={handleChange}
          captionElement={({ date, localeUtils }) => (
            <YearMonthPicker
              date={date}
              localeUtils={localeUtils}
              onChange={setMonth}
            />
          )}
          {...additionalProps}
        />
        {!useRange && !hideTime && (
          <TimePicker defaultValue={day} onChange={handleTimeChange} />
        )}
      </PickerContents>
    </Picker>
  )
}

DatePicker.propTypes = {
  defaultValue: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  disabled: PropTypes.bool,
  errors: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
  hasError: PropTypes.bool,
  hideClear: PropTypes.bool,
  name: PropTypes.string,
  noFlex: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string,
  required: PropTypes.bool,
  useRange: PropTypes.bool,
}

export default DatePicker
